import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button, DataTable, EmptyState, FilterBar, FiltroPill, FormActions, FormGrid, IconButton,
  NumberField, Pagination, SelectField, TextField, type Column,
} from "@atlas/ui/web";
import { POR_PAGINA_PADRAO, recorteDoServidor } from "../../lib/recorte";
import { useFiltros } from "../../lib/use-filtros";
import { atlas } from "../../lib/sdk";
import { QUERY_UNIDADES_OPERAVEIS, listarUnidadesOperaveis } from "../../lib/unidades-operaveis";
import type { AdminIdUnicoConfig, IdUnicoFormato } from "@atlas/sdk";

export function AdminIdUnico() {
  const [prefFiltro, setPrefFiltro] = useState("");
  const [pagina, setPagina] = useState(1);
  const [porPagina, setPorPagina] = useState(POR_PAGINA_PADRAO);
  const configs = useQuery({
    queryKey: ["admin", "id-unico", prefFiltro, pagina, porPagina],
    queryFn: () => atlas.admin.listIdUnicoConfigs({
      prefeitura_id: prefFiltro ? Number(prefFiltro) : undefined,
      limit: porPagina,
      offset: (pagina - 1) * porPagina,
    }),
    // Sem isto a tabela pisca vazia a cada virada de pagina.
    placeholderData: (anterior) => anterior,
  });
  // A lista INTEIRA de prefeituras: alimenta o chip de filtro e o dropdown do
  // modal. Sem `limit`, a rota cai no caminho C1 e devolve tudo — e e isso que
  // o filtro precisa, ja que a mae do filtro nao pode encolher com a pagina.
  const prefeituras = useQuery({ queryKey: QUERY_UNIDADES_OPERAVEIS, queryFn: listarUnidadesOperaveis });
  const [editing, setEditing] = useState<AdminIdUnicoConfig | "new" | null>(null);
  const filtros = useFiltros(
    [{ nome: "prefeitura", ligado: prefFiltro !== "", limpar: () => setPrefFiltro("") }],
    setPagina,
  );
  /** Mexer no filtro volta pra primeira pagina. */
  const filtrar = filtros.filtrar(setPrefFiltro);
  // A pagina JA vem fatiada e ordenada pela chave (unidadeId), que e a ordem
  // que `loadCollection` produzia antes — a tela nao muda de ordem.
  const daPagina = recorteDoServidor(configs.data?.configs ?? [], configs.data?.total ?? 0, pagina, porPagina);
  // Botao "emitir manualmente" removido da UI (20/07/2026) — em operacao normal
  // cada aprovacao de proposta ja consome o proximo ID automaticamente, e o
  // clique acidental no botao "+" queimava numeros da sequencia sem gerar
  // operacao real. Endpoint POST /v1/admin/id-unico/issue continua disponivel
  // pra scripts/migracao manual (nao ha caminho de UI).

  const columns: Column<AdminIdUnicoConfig>[] = [
    { key: "prefeituraNome", header: "Órgão/entidade" },
    { key: "prefixo", header: "Prefixo", mono: true },
    { key: "formato", header: "Formato" },
    { key: "larguraSeq", header: "Largura seq.", align: "right" },
    { key: "proximoSeq", header: "Próximo seq.", align: "right" },
    { key: "exemplo", header: "Próximo ID (preview)", mono: true },
    { key: "atualizadoEm", header: "Atualizado em", render: (c) => new Date(c.atualizadoEm).toLocaleString("pt-BR") },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>ID único / parametrização de chave</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 720 }}>
            Configura como o ID único de cada operação é gerado por organização. Composto por prefixo + sequencial (ou hash). Usado no ADF e no bate-de-carteira.
          </p>
        </div>
        <Button onClick={() => setEditing("new")}>+ Configurar organização</Button>
      </header>

      {/* PREFEITURA SAIU DE CHIPS PARA PILL PESQUISAVEL.
          A tela nao tinha filtro nenhum, ganhou chips, e os chips eram o
          controle errado para esta dimensao: e' UMA linha por prefeitura
          cadastrada, e a averbadora atende dezenas — a faixa de chips crescia
          sem teto e virava um bloco que o operador varre com o olho em vez de
          filtrar. A pill mantem a dimensao inteira num controle e ganha campo
          de busca acima de 8 opcoes.
          A busca por prefixo/preview (identificador tecnico) depende da rota
          aceitar `q`, e fica para quando esta consulta for mexida. */}
      <FilterBar {...filtros.propsFilterBar}>
        <FiltroPill
          label="Órgão ou entidade"
          opcoes={(prefeituras.data?.prefeituras ?? []).map((p) => ({ value: String(p.id), label: p.nome }))}
          // A rota aceita UMA prefeitura. Comparar duas configuracoes de ID
          // unico lado a lado nao e' a tarefa: aqui se confere ou se corrige a
          // regra de uma unidade.
          selecionados={prefFiltro ? [prefFiltro] : []}
          onChange={(vs: string[]) => filtrar(vs[vs.length - 1] ?? "")}
          buscaPlaceholder="Buscar organização..."
          carregando={prefeituras.isLoading}
        />
      </FilterBar>

      <DataTable
        columns={columns}
        rows={daPagina.itens}
        rowKey={(c) => String(c.unidadeId)}
        loading={configs.isLoading}
        // Distinguir "nada bate com o filtro" de "nao ha configuracao".
        emptyState={
          configs.isError
            ? "Não foi possível carregar as configurações de ID único."
            : <EmptyState
                motivo={filtros.temFiltro ? "filtro-vazio" : "sem-dados"}
                filtroVazio="Esta organização ainda não tem formato de ID único configurado."
                semDados="Nenhuma organização com formato de ID único configurado ainda."
                onLimpar={filtros.limparFiltros}
              />
        }
        actions={(c) => (
          <IconButton title="Editar" onClick={() => setEditing(c)}>✎</IconButton>
        )}
      />

      <Pagination
        total={daPagina.total}
        pagina={daPagina.pagina}
        porPagina={porPagina}
        onPagina={setPagina}
        onPorPagina={setPorPagina}
        rotulo="configurações"
      />

      {editing ? (
        <ConfigModal
          initial={editing === "new" ? null : editing}
          prefeituras={prefeituras.data?.prefeituras ?? []}
          onClose={() => setEditing(null)}
        />
      ) : null}
    </div>
  );
}

function ConfigModal({
  initial, prefeituras, onClose,
}: {
  initial: AdminIdUnicoConfig | null;
  prefeituras: { id: number; nome: string; uf: string }[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    unidadeId: initial?.unidadeId ?? prefeituras[0]?.id ?? 1,
    prefixo: initial?.prefixo ?? "",
    formato: (initial?.formato ?? "SEQ") as IdUnicoFormato,
    larguraSeq: initial?.larguraSeq ?? 6,
    proximoSeq: initial?.proximoSeq ?? 1,
    separador: initial?.separador ?? "-",
  });
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => atlas.admin.upsertIdUnicoConfig(form),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "id-unico"] });
      onClose();
    },
    onError: (err) => setError(err instanceof Error ? err.message : "Erro ao salvar"),
  });
  return (
    <div onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }} style={modalBackdrop}>
      <div onClick={(e) => e.stopPropagation()} style={modalCard}>
        <h3 style={{ margin: 0 }}>{initial ? `Editar — ${initial.prefeituraNome}` : "Nova configuração de ID único"}</h3>
        <FormGrid cols={2}>
          <SelectField
            label="Órgão ou entidade"
            value={String(form.unidadeId)}
            onChange={(e) => setForm({ ...form, unidadeId: Number(e.target.value) })}
            options={prefeituras.map((p) => ({ value: String(p.id), label: `${p.nome}/${p.uf}` }))}
            disabled={!!initial}
          />
          <TextField
            label="Prefixo (2-8 caracteres, A-Z/0-9)"
            value={form.prefixo}
            maxLength={8}
            onChange={(e) => setForm({ ...form, prefixo: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "") })}
            required
          />
          <SelectField
            label="Formato"
            value={form.formato}
            onChange={(e) => setForm({ ...form, formato: e.target.value as IdUnicoFormato })}
            options={[
              { value: "SEQ", label: "Prefixo + Sequencial (PLH-000001)" },
              { value: "YYYYMM_SEQ", label: "Prefixo + AAAAMM + Sequencial (PLH-202606-00001)" },
              { value: "SEQ_HASH", label: "Prefixo + Sequencial + Hash (PLH-00001-AB12CD)" },
            ]}
          />
          <NumberField label="Largura da sequência (zeros)" value={form.larguraSeq} min={3} max={10} onChange={(e) => setForm({ ...form, larguraSeq: Number(e.target.value) })} />
          <NumberField label="Próximo sequencial" value={form.proximoSeq} min={1} onChange={(e) => setForm({ ...form, proximoSeq: Number(e.target.value) })} />
          <TextField label="Separador" value={form.separador} maxLength={2} onChange={(e) => setForm({ ...form, separador: e.target.value })} />
        </FormGrid>
        {error ? <div style={{ color: "var(--danger-500)", fontSize: 13 }}>{error}</div> : null}
        <FormActions>
          <Button variant="ghost" type="button" onClick={onClose}>Cancelar</Button>
          <Button type="button" disabled={save.isPending} onClick={() => save.mutate()}>{save.isPending ? "Salvando..." : "Salvar"}</Button>
        </FormActions>
      </div>
    </div>
  );
}

const modalBackdrop: React.CSSProperties = {
  position: "fixed", inset: 0, background: "rgba(10,22,40,.6)",
  display: "grid", placeItems: "center", zIndex: 100, backdropFilter: "blur(6px)",
};
const modalCard: React.CSSProperties = {
  background: "var(--bg-elev)", border: "1px solid var(--border-strong)",
  borderRadius: 14, padding: 24, maxWidth: 640, width: "calc(100% - 48px)",
  display: "flex", flexDirection: "column", gap: 16, boxShadow: "var(--shadow-lg)",
  // Modal alto (form longo) precisa rolar dentro de si — sem isto o rodape
  // fica fora da viewport e nao da pra chegar nos botoes. Cliente 05/08/2026.
  maxHeight: "calc(100vh - 48px)",
  overflowY: "auto",
};
